import axios from 'axios';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface Notification {
  notificationId: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  relatedId?: string;
  relatedType?: 'booking' | 'negotiation' | 'payment';
  read: boolean;
  createdAt: string;
}

export interface NotificationListResponse {
  notifications: Notification[];
  count: number;
  lastEvaluatedKey?: string;
}

export interface ListNotificationsParams {
  limit?: number;
  lastEvaluatedKey?: string;
  search?: string;
  userId?: string;
  type?: string;
  read?: boolean;
  startDate?: string;
  endDate?: string;
}

export interface SendNotificationRequest {
  userId: string;
  type: string;
  title: string;
  message: string;
  relatedId?: string;
  relatedType?: 'booking' | 'negotiation' | 'payment';
}

class NotificationsManagementService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  private async getAuthHeaders() {
    const token = adminAuthService.getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    return {
      Authorization: `Bearer ${token}`,
    };
  }

  async listNotifications(params?: ListNotificationsParams): Promise<NotificationListResponse> {
    const headers = await this.getAuthHeaders();
    const queryParams = new URLSearchParams();
    
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.lastEvaluatedKey) queryParams.append('lastEvaluatedKey', params.lastEvaluatedKey);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.type) queryParams.append('type', params.type);
    if (params?.read !== undefined) queryParams.append('read', params.read.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const response = await this.api.get<{data: NotificationListResponse}>(
      `/admin/notifications?${queryParams.toString()}`,
      { headers }
    );
    return response.data.data;
  }

  async getNotification(notificationId: string): Promise<Notification> {
    const headers = await this.getAuthHeaders();
    const response = await this.api.get<{data: {notification: Notification}}>(
      `/admin/notifications/${notificationId}`,
      { headers }
    );
    return response.data.data.notification;
  }

  async sendNotification(request: SendNotificationRequest): Promise<Notification> {
    const headers = await this.getAuthHeaders();
    const response = await this.api.post<{data: {notification: Notification}}>(
      `/admin/notifications`,
      request,
      { headers }
    );
    return response.data.data.notification;
  }
}

export const notificationsManagementService = new NotificationsManagementService();
